import { useSEO } from "@/hooks/useSEO";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import DeliveryDiagram from "@/components/DeliveryDiagram";
import { Reveal } from "@/components/Reveal";
import { PRINCIPLES } from "@/data/content";

const ABOUT_IMG = "/about-outsourcing.png";

export default function About() {
  useSEO(
    "About Synergy Outsourcing | Australian Company, Globally Delivered",
    "Synergy Outsourcing is an Australian company created to help businesses access capable professionals without compromising on quality, communication or accountability."
  );
  return (
    <>
      <PageHero
        eyebrow="About Synergy Outsourcing"
        lines={["Australian company.", "Australian-led.", "Built around accountability."]}
        copy="Synergy Outsourcing was created to help businesses access capable professionals without compromising on quality, communication or accountability."
      />
      <section className="py-14 sm:py-16" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal className="hidden lg:block">
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img
                src={ABOUT_IMG}
                alt="Two professional teams connected across a global map, depicting managed outsourcing"
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-display text-2xl font-bold leading-snug tracking-tight text-white sm:text-3xl">
              A managed talent partner, not a resume farm.
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-mist">
              <p>
                Most outsourcing is sold as a transaction: send a job description, receive a person, hope for the best.
                We built Synergy Outsourcing around a different idea: growing businesses don't need more resumes, they
                need a partner who builds and manages a professional team for them.
              </p>
              <p>
                We are an Australian company, and proudly Australian-led. Our model combines Australian management
                with global delivery, which means clear communication, structured oversight and genuine accountability
                on our side, and dedicated professionals who become a real extension of your business on yours.
              </p>
              <p>
                We operate across Australia, the US and Canada, helping growing businesses access capacity without the
                cost and complexity of building every capability in-house.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="bg-ink-2/40 py-14 sm:py-16" data-testid="about-principles">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="How we operate" title="Principles behind every engagement." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <div className="card-hover h-full rounded-2xl border border-white/10 bg-ink p-7">
                  <span className="font-mono text-sm font-semibold text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="py-14 sm:py-16" data-testid="about-delivery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Our delivery model"
            title="One accountable line from your business to your team."
            align="center"
          />
          <div className="mt-14">
            <DeliveryDiagram />
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
